import { useParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { numberFormat } from '../format';
import type { Span } from '../perSecond';

/** What every chart of the page shares: the time axis, the not-measured, stalled and background spans and the load-change markers. */
export interface TimeAxis {
    /** First and last second shown, inclusive, since the start of the run. */
    from: number;
    to: number;
    /** Seconds per slot: 1 for the last ten minutes, more when the whole run is compressed. */
    slot: number;
    unmeasured: Span[];
    /** Seconds in which the browser's main thread stalled: its probes were not judged. Drawn over the not-measured spans. */
    stalled: Span[];
    background: Span[];
    marks: number[];
}

export interface ChartScales {
    /** x of the left edge of a second. */
    x: (second: number) => number;
    y: (value: number) => number;
    width: number;
    height: number;
}

const TOP = 8;
const BACKGROUND_STRIP = 3;

/** Seconds since the start of the run as m:ss, or h:mm:ss past the hour. */
export function formatRunTime(seconds: number): string {
    const s = Math.max(0, Math.round(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = String(s % 60).padStart(2, '0');
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** A rounded-up axis maximum, never below `floor`, so an all-zero chart scales flat instead of NaN. */
export function axisMax(values: number[], floor: number): number {
    const max = Math.max(floor, ...values);
    return scaleLinear<number>({ domain: [0, max], nice: true }).domain()[1];
}

/**
 * The frame of one per-second chart, in the style of UsageBarChart: visx scales on the container's
 * width, Tailwind fill/stroke classes so it follows the theme, a <figure> with a caption and an
 * `img` role whose label states the latest values.
 */
export const TimeChart = ({
    title,
    legend,
    ariaLabel,
    axis,
    yMax,
    unit,
    children
}: {
    title: string;
    legend: ReactNode;
    ariaLabel: string;
    axis: TimeAxis;
    yMax: number;
    unit: string;
    children: (scales: ChartScales) => ReactNode;
}) => {
    const { i18n } = useTranslation();
    const { parentRef, width, height } = useParentSize({ debounceTime: 150 });
    const xScale = scaleLinear<number>({ domain: [axis.from, axis.to + 1], range: [0, width] });
    const yScale = scaleLinear<number>({ domain: [0, yMax], range: [height, TOP], clamp: true });
    const x = (second: number) => xScale(second) ?? 0;
    const y = (value: number) => yScale(value) ?? 0;
    const within = (s: { from: number; to: number }) => s.to > axis.from && s.from <= axis.to;
    const spanRect = (s: { from: number; to: number }) => {
        const left = x(Math.max(s.from, axis.from));
        return { x: left, width: Math.max(1, x(Math.min(s.to, axis.to + 1)) - left) };
    };

    return (
        <figure className="w-full min-w-0">
            <figcaption className="flex flex-wrap items-baseline justify-between gap-x-4 mb-1">
                <span className="text-sm font-semibold">{title}</span>
                <span className="flex flex-wrap gap-x-3 text-xs">{legend}</span>
            </figcaption>
            <div ref={parentRef} className="w-full h-32 border-b border-gray-300">
                <svg width={width} height={height} role="img" aria-label={ariaLabel}>
                    {axis.unmeasured.filter(within).map((s) => (
                        <rect key={`u${s.from}`} {...spanRect(s)} y={0} height={height} className="fill-gray-200" />
                    ))}
                    {axis.stalled.filter(within).map((s) => (
                        <rect key={`s${s.from}`} {...spanRect(s)} y={0} height={height} className="fill-warning-light" />
                    ))}
                    {width > 0 && height > 0 && children({ x, y, width, height })}
                    {axis.marks
                        .filter((m) => m >= axis.from && m <= axis.to)
                        .map((m) => (
                            <line key={`m${m}`} x1={x(m)} x2={x(m)} y1={0} y2={height} strokeDasharray="3 3" className="stroke-gray-500" />
                        ))}
                    {axis.background.filter(within).map((s) => (
                        <rect key={`b${s.from}`} {...spanRect(s)} y={0} height={BACKGROUND_STRIP} className="fill-accent" />
                    ))}
                    <text x={2} y={TOP + 2} dominantBaseline="hanging" className="fill-current text-[10px] opacity-70">
                        {`${numberFormat(i18n.language).format(yMax)} ${unit}`}
                    </text>
                </svg>
            </div>
            <div className="flex justify-between text-xs opacity-70 mt-1">
                <span>{formatRunTime(axis.from)}</span>
                <span>{formatRunTime(axis.to + 1)}</span>
            </div>
        </figure>
    );
};

/** A legend entry: a swatch in the series' colour and its name. */
export const LegendItem = ({ swatch, label }: { swatch: string; label: string }) => (
    <span className="inline-flex items-center gap-1">
        <span aria-hidden="true" className={`inline-block size-2.5 rounded-xs ${swatch}`} />
        {label}
    </span>
);
